import { formDraftDb, type FormDraftRow, type DraftSessionRow } from "@/lib/formDraftDb"
import { environmentStorageKey } from "@/lib/appEnvironment"
import { useAuthStore } from "@/store/authStore"
import type { FieldValues } from "react-hook-form"
import { withSubmissionLock } from "@/services/submissionConcurrency"

export type DraftIdentity = { userId: string; workspaceId: string; submissionId: string }
export const draftKeyFor = (id: DraftIdentity) => [id.userId, id.workspaceId, id.submissionId].join(":")
export const activeDrafts = new Set<string>()
const writes = new Map<string, Promise<unknown>>()
const failures = new Map<string, unknown>()
const draftChannel = typeof window !== "undefined" && typeof BroadcastChannel !== "undefined"
  ? new BroadcastChannel(environmentStorageKey("draft-changes")) : undefined
if (draftChannel) draftChannel.onmessage = () => window.dispatchEvent(new Event("dkro-drafts-changed"))
export function notifyDraftsChanged() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event("dkro-drafts-changed"))
  draftChannel?.postMessage(null)
}

export function assertDraftOwner(userId: string) {
  if (!userId || useAuthStore.getState().user?.id !== userId) throw new Error("A sessão mudou. Seu trabalho continua salvo para o respectivo dono.")
}

export function mutateDraft<T>(key: string, operation: () => Promise<T>): Promise<T> {
  const previous = writes.get(key) ?? Promise.resolve()
  const next = previous.catch(() => undefined).then(() => withSubmissionLock(`draft-write:${key}`, operation))
  writes.set(key, next)
  void next.then(() => {
    failures.delete(key)
    notifyDraftsChanged()
  }, error => { failures.set(key, error) }).finally(() => {
    if (writes.get(key) === next) writes.delete(key)
  })
  return next
}

export function hasPendingDraftWrite(key: string): boolean {
  return writes.has(key) || failures.has(key)
}

export async function flushDraftWrites(key?: string): Promise<void> {
  const userId = useAuthStore.getState().user?.id
  const matches = (candidate: string) => key ? candidate === key : !!userId && candidate.startsWith(`${userId}:`)
  for (;;) {
    const pending = [...writes].filter(([candidate]) => matches(candidate)).map(([, write]) => write)
    if (!pending.length) break
    await Promise.all(pending)
  }
  const failure = [...failures].find(([candidate]) => matches(candidate))?.[1]
  if (failure) throw failure
}

export async function saveDraftValues(key: string, values: FieldValues, metadata: Partial<FormDraftRow> = {}): Promise<FormDraftRow> {
  const data = structuredClone(values)
  const definedMetadata = Object.fromEntries(Object.entries(metadata).filter(([, value]) => value !== undefined))
  return mutateDraft(key, async () => {
    const previous = await formDraftDb.drafts.get(key)
    if (metadata.editingSessionId) await assertEditingSession(key, metadata.editingSessionId)
    else if (previous?.editingSessionId) throw new Error("É necessária uma sessão de edição para alterar este rascunho.")
    if (previous?.completionRequestedAt || previous?.deletionConfirmed || (previous?.status === "completed" && !previous.submissionId?.startsWith("event-action:"))) throw new Error("Esta resposta está congelada para conclusão ou remoção.")
    const userId = metadata.userId ?? previous?.userId
    if (!userId) throw new Error("Não é possível salvar um rascunho sem dono.")
    // Ownership is captured when the write is scheduled; logout does not cancel local writes.
    const changed = JSON.stringify(previous?.data) !== JSON.stringify(data)
    const row: FormDraftRow = {
      ...previous, ...definedMetadata, key, userId, data,
      revision: changed ? crypto.randomUUID() : (previous?.revision ?? crypto.randomUUID()),
      updatedAt: Date.now(),
      status: changed ? "editing" : (metadata.status ?? previous?.status ?? "editing"),
    }
    await formDraftDb.drafts.put(row)
    return row
  })
}

export async function readOwnedDraft(id: DraftIdentity): Promise<FormDraftRow | undefined> {
  assertDraftOwner(id.userId)
  await flushDraftWrites(draftKeyFor(id))
  const row = await formDraftDb.drafts.get(draftKeyFor(id))
  assertDraftOwner(id.userId)
  return row?.userId === id.userId && row.workspaceId === id.workspaceId ? row : undefined
}

export async function listOwnedDrafts(userId: string, workspaceId: string, taskId?: string) {
  assertDraftOwner(userId)
  const rows = await formDraftDb.drafts.where("[userId+workspaceId]").equals([userId, workspaceId]).toArray()
  assertDraftOwner(userId)
  return rows.filter(row => !!row.taskId && row.status !== "completed" && !row.deletionConfirmed && !row.submissionId?.startsWith("event-action:") && (!taskId || row.taskId === taskId))
}

export async function acknowledgeDraft(id: DraftIdentity, revision: string, serverUpdatedAt: string | undefined, status: "paused" | "completed", cosmosAnswerDocId?: string) {
  const key = draftKeyFor(id)
  await mutateDraft(key, async () => {
    const row = await formDraftDb.drafts.get(key)
    if (!row || row.revision !== revision) return
    await formDraftDb.drafts.put({ ...row, syncedRevision: revision, serverUpdatedAt, status, cosmosAnswerDocId })
  })
}

export async function assertEditingSession(key: string, sessionId: string) {
  const session = await formDraftDb.sessions.get(key)
  if (!session || session.sessionId !== sessionId || session.phase !== "editing")
    throw new Error("Esta sessão de edição está encerrada. Reabra o rascunho salvo antes de fazer alterações.")
  const { hasAcceptedSession } = await import("@/stores/useSubmissionQueueStore")
  if (await hasAcceptedSession(key, sessionId)) throw new Error("Esta sessão de edição já foi colocada na fila e não pode ser alterada.")
}

/** Persist the rollback point before autosave or suspension of an earlier Pause. */
export async function beginDraftSession(id: DraftIdentity, initial: FormDraftRow, baseline: FormDraftRow | null) {
  const key = draftKeyFor(id)
  return withSubmissionLock(key, () => mutateDraft(key, () => formDraftDb.transaction("rw", formDraftDb.drafts, formDraftDb.sessions, async () => {
    assertDraftOwner(id.userId)
    const existing = await formDraftDb.sessions.get(key)
    if (existing?.phase === "discarding") throw new Error("A recuperação do descarte está pendente. Tente novamente antes de editar.")
    const current = await formDraftDb.drafts.get(key)
    if (current?.completionRequestedAt || current?.deletionConfirmed || current?.status === "completed") throw new Error("Esta resposta está congelada para conclusão ou remoção.")
    if (existing?.phase === "editing" && current) return { session: existing, draft: current }
    const session: DraftSessionRow = { ...id, key, sessionId: crypto.randomUUID(), phase: "editing", baseline: structuredClone(baseline) }
    const draft = { ...(current ?? initial), ...id, key, editingSessionId: session.sessionId }
    await formDraftDb.sessions.put(session)
    await formDraftDb.drafts.put(draft)
    return { session, draft }
  })))
}

export async function beginDiscardSession(id: DraftIdentity, sessionId: string) {
  const key = draftKeyFor(id)
  await mutateDraft(key, async () => {
    assertDraftOwner(id.userId)
    const session = await formDraftDb.sessions.get(key)
    if (!session || session.sessionId !== sessionId || session.phase === "committed") throw new Error("A sessão de edição mudou.")
    if (session.phase === "editing") await assertEditingSession(key, sessionId)
    if (session.phase !== "discarded") await formDraftDb.sessions.put({ ...session, phase: "discarding" })
  })
}

export async function rollbackDraftSession(id: DraftIdentity, sessionId: string) {
  const key = draftKeyFor(id)
  await beginDiscardSession(id, sessionId)
  await withSubmissionLock(key, async () => {
    assertDraftOwner(id.userId)
    const session = await formDraftDb.sessions.get(key)
    if (!session || session.sessionId !== sessionId) throw new Error("A sessão de edição mudou.")
    const { restoreSessionPause } = await import("@/stores/useSubmissionQueueStore")
    const { discardNativeSession } = await import("@/services/nativeCaptureService")
    await discardNativeSession(id, sessionId)
    const { collectLocalMediaIds, stagedMediaFor, releaseUnreferencedMedia } = await import("@/services/offlineMediaService")
    if (session.phase !== "discarded") {
      await restoreSessionPause(session)
      const current = await formDraftDb.drafts.get(key)
      const original = collectLocalMediaIds(session.baseline?.data)
      const added = collectLocalMediaIds(current?.data)
      await withSubmissionLock(`media-stage:${key}`, async () => {
        for (const value of await stagedMediaFor(id)) added.add(value.localMediaId)
      })
      for (const media of original) added.delete(media)
      await mutateDraft(key, () => formDraftDb.transaction("rw", formDraftDb.drafts, formDraftDb.sessions, async () => {
        assertDraftOwner(id.userId)
        if (session.baseline) await formDraftDb.drafts.put(session.baseline)
        else await formDraftDb.drafts.delete(key)
        await formDraftDb.sessions.put({ ...session, phase: "discarded", baseline: null, suspendedPause: undefined, addedMediaIds: [...added] })
      }))
    }
    const discarded = await formDraftDb.sessions.get(key)
    await releaseUnreferencedMedia(discarded?.addedMediaIds ?? [], sessionId)
  })
}

// Caller holds the submission lock and has confirmed server deletion (or a never-sent local draft).
export async function finishSubmissionDeletion(id: DraftIdentity) {
  const key = draftKeyFor(id)
  const { removeSubmissionSummary } = await import("@/services/offlineSummaryService")
  const { retireSubmissionQueue } = await import("@/stores/useSubmissionQueueStore")
  const { stagedMediaFor, collectLocalMediaIds, releaseUnreferencedMedia } = await import("@/services/offlineMediaService")
  assertDraftOwner(id.userId)
  const row = await formDraftDb.drafts.get(key)
  if (row && !row.deletionConfirmed) throw new Error("A exclusão deve ser confirmada antes de remover os dados locais.")
  const media = collectLocalMediaIds(row?.data, new Set(row?.cleanupMediaIds ?? []))
  for (const value of await stagedMediaFor(id)) media.add(value.localMediaId)
  await removeSubmissionSummary(id.userId, id.workspaceId, id.submissionId)
  await retireSubmissionQueue(key, id.userId)
  await mutateDraft(key, () => formDraftDb.transaction("rw", formDraftDb.drafts, formDraftDb.sessions, async () => {
    const session = await formDraftDb.sessions.get(key)
    if (session) await formDraftDb.sessions.put({ ...session, phase: "discarded", baseline: null, suspendedPause: undefined })
    if (row) await formDraftDb.drafts.put({ ...row, status: "completed", data: {}, form: undefined, cleanupMediaIds: [...media] })
  }))
  await releaseUnreferencedMedia(media)
  if (!(await stagedMediaFor(id)).some(value => media.has(value.localMediaId))) await mutateDraft(key, () => formDraftDb.drafts.delete(key))
}

export async function commitDraftSession(id: DraftIdentity, sessionId: string) {
  const key = draftKeyFor(id)
  await mutateDraft(key, async () => {
    assertDraftOwner(id.userId)
    const session = await formDraftDb.sessions.get(key)
    if (!session || session.sessionId !== sessionId || session.phase !== "editing") throw new Error("A sessão de edição mudou.")
    await formDraftDb.sessions.put({ ...session, phase: "committed", baseline: null, suspendedPause: undefined })
  })
}

export async function migrateVerifiedDraft(id: DraftIdentity): Promise<FormDraftRow | undefined> {
  // Caller must first verify server ownership (or an already-owned outbox item).
  const owned = await readOwnedDraft(id)
  if (owned) return owned
  const legacy = (await formDraftDb.drafts.toArray()).filter(row => !row.userId
    && row.key.startsWith(`${id.workspaceId}:`) && row.key.endsWith(`:${id.submissionId}`))
    .sort((a, b) => b.updatedAt - a.updatedAt)[0]
  if (!legacy) return undefined
  const key = draftKeyFor(id)
  return mutateDraft(key, async () => {
    assertDraftOwner(id.userId)
    const existing = await formDraftDb.drafts.get(key)
    if (existing) return existing
    const row = { ...legacy, ...id, key, revision: crypto.randomUUID(), status: "editing" as const }
    await formDraftDb.drafts.put(row)
    const verified = await formDraftDb.drafts.get(key)
    if (JSON.stringify(verified) !== JSON.stringify(row)) throw new Error("Falha na verificação da migração do rascunho. O original foi mantido.")
    const source = await formDraftDb.drafts.get(legacy.key)
    if (JSON.stringify(source) === JSON.stringify(legacy)) await formDraftDb.drafts.delete(legacy.key)
    return row
  })
}
